'use client';

import React, { useState } from 'react';
import Link from 'next/link';
import Icon from '@/components/ui/AppIcon';

interface FooterProps {
  className?: string;
}

const Footer = ({ className = '' }: FooterProps) => {
  const [email, setEmail] = useState('');

  const footerLinks = {
    diensten: [
      { label: 'AI Chatbots', href: '/service-detail' },
      { label: 'Social Media AI', href: '/service-detail' },
      { label: 'Voorspellende Analytics', href: '/service-detail' },
      { label: 'Procesautomatisering', href: '/service-detail' },
    ],
    bedrijf: [
      { label: 'Over ons', href: '/about-us' },
      { label: 'Case Studies', href: '/case-studies-hub' },
      { label: 'Blog', href: '/blog' },
      { label: 'Carrière', href: '/about-us' },
    ],
    support: [
      { label: 'Contact', href: '/contact' },
      { label: 'FAQ', href: '/faq' },
      { label: 'Documentatie', href: '/blog' },
      { label: 'Prijzen', href: '/pricing' },
    ],
    juridisch: [
      { label: 'Privacy Policy', href: '/faq' },
      { label: 'Algemene Voorwaarden', href: '/faq' },
      { label: 'Cookie Policy', href: '/faq' },
      { label: 'GDPR', href: '/faq' },
    ],
  };

  const socialLinks = [
    { name: 'LinkedIn', icon: 'LinkIcon', href: '#' },
    { name: 'Twitter', icon: 'LinkIcon', href: '#' },
    { name: 'Facebook', icon: 'LinkIcon', href: '#' },
    { name: 'Instagram', icon: 'LinkIcon', href: '#' },
  ];

  return (
    <footer className={`bg-gradient-to-b from-background to-[#0a0a0a] border-t border-border ${className}`}>
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-12 lg:py-16">
        {/* Main Footer Content */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-8 lg:gap-12 mb-12">
          {/* Brand Column */}
          <div className="lg:col-span-2">
            <Link href="/homepage" className="flex items-center space-x-3 mb-6">
              <div className="relative">
                <div className="absolute inset-0 bg-primary rounded-lg blur-md opacity-50"></div>
                <div className="relative bg-gradient-to-br from-primary to-secondary p-2 rounded-lg">
                  <svg
                    width="32"
                    height="32"
                    viewBox="0 0 32 32"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                    className="text-background"
                  >
                    <path
                      d="M16 4L4 10L16 16L28 10L16 4Z"
                      fill="currentColor"
                      fillOpacity="0.9"
                    />
                    <path
                      d="M4 16L16 22L28 16"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <path
                      d="M4 22L16 28L28 22"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </div>
              </div>
              <div className="flex flex-col">
                <span className="text-xl font-display font-bold text-foreground">
                  AIM Agency
                </span>
                <span className="text-xs font-mono text-muted-foreground">
                  AI Solutions
                </span>
              </div>
            </Link>
            <p className="text-sm text-muted-foreground mb-6 max-w-sm">
              AI-oplossingen die direct werken voor jouw bedrijf. Van social media tot klantenservice - complete implementaties voor Benelux MKB.
            </p>
            <div className="flex space-x-3">
              {socialLinks.map((social) => (
                <a
                  key={social.name}
                  href={social.href}
                  className="w-10 h-10 rounded-lg glassmorphic flex items-center justify-center hover:bg-primary/10 hover:text-primary transition-all duration-300"
                  aria-label={social.name}
                >
                  <Icon name={social.icon as any} size={20} />
                </a>
              ))}
            </div>
          </div>

          {/* Links Columns */}
          <div>
            <h3 className="text-sm font-semibold text-foreground mb-4">Diensten</h3>
            <ul className="space-y-3">
              {footerLinks.diensten.map((link) => (
                <li key={link.label}>
                  <Link
                    href={link.href}
                    className="text-sm text-muted-foreground hover:text-primary transition-colors"
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h3 className="text-sm font-semibold text-foreground mb-4">Bedrijf</h3>
            <ul className="space-y-3">
              {footerLinks.bedrijf.map((link) => (
                <li key={link.label}>
                  <Link
                    href={link.href}
                    className="text-sm text-muted-foreground hover:text-primary transition-colors"
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h3 className="text-sm font-semibold text-foreground mb-4">Support</h3>
            <ul className="space-y-3">
              {footerLinks.support.map((link) => (
                <li key={link.label}>
                  <Link
                    href={link.href}
                    className="text-sm text-muted-foreground hover:text-primary transition-colors"
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h3 className="text-sm font-semibold text-foreground mb-4">Juridisch</h3>
            <ul className="space-y-3">
              {footerLinks.juridisch.map((link) => (
                <li key={link.label}>
                  <Link
                    href={link.href}
                    className="text-sm text-muted-foreground hover:text-primary transition-colors"
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Newsletter Section */}
        <div className="glassmorphic rounded-2xl p-6 lg:p-8 mb-12" suppressHydrationWarning>
          <div className="flex flex-col lg:flex-row items-center justify-between space-y-4 lg:space-y-0">
            <div className="text-center lg:text-left">
              <h3 className="text-xl font-display font-bold text-foreground mb-2">
                Blijf op de hoogte van AI-trends
              </h3>
              <p className="text-sm text-muted-foreground">
                Ontvang maandelijks praktische AI-inzichten voor jouw bedrijf
              </p>
            </div>
            <div className="flex w-full lg:w-auto space-x-2">
              <input
                type="email"
                placeholder="jouw@email.nl"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                suppressHydrationWarning
                className="flex-1 lg:w-64 px-4 py-3 rounded-lg bg-input border border-border text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary"
              />
              <button 
                suppressHydrationWarning
                className="px-6 py-3 rounded-lg font-semibold bg-primary text-primary-foreground hover:bg-primary/90 transition-all duration-300 whitespace-nowrap"
              >
                Inschrijven
              </button>
            </div>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="pt-8 border-t border-border">
          <div className="flex flex-col md:flex-row items-center justify-between space-y-4 md:space-y-0">
            <p className="text-sm text-muted-foreground text-center md:text-left">
              © 2026 AIM Agency. Alle rechten voorbehouden.
            </p>
            <div className="flex flex-wrap items-center justify-center gap-4 text-xs text-muted-foreground">
              <div className="flex items-center space-x-2">
                <Icon name="MapPinIcon" size={14} className="text-primary" />
                <span>Amsterdam, Nederland</span>
              </div>
              <div className="flex items-center space-x-2">
                <Icon name="EnvelopeIcon" size={14} className="text-primary" />
                <a href="mailto:info@aimagency.nl" className="hover:text-primary transition-colors">
                  info@aimagency.nl
                </a>
              </div>
              <div className="flex items-center space-x-2">
                <Icon name="PhoneIcon" size={14} className="text-primary" />
                <a href="tel:+31201234567" className="hover:text-primary transition-colors">
                  +31 20 123 4567
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;